/**
 * Contract constants for the deployed Coffee Rewards package on Sui testnet.
 * All modules share a single package ID.
 */

// v5 (2026-07-05) — referral 3-purchase gate (§4), earnable badges (§5),
// on-chain validated reward menu (§6). BREAKING redeploy: fresh package, so
// pre-v5 customer objects (typed to the old package) don't carry over.
//
// IDs now come from the single per-network map (spec Phase 2 item 1) so the
// client and server can never drift — see src/config/sui-networks.ts.
import { resolveNetworkIds } from '../config/sui-networks';

const IDS = resolveNetworkIds((import.meta.env.VITE_SUI_NETWORK || 'testnet').trim());
/** Call target — address move calls here. Changes on every package upgrade. */
export const PACKAGE_ID = IDS.packageId;
/**
 * Type identity — build StructType filters and event types from this, NEVER
 * from PACKAGE_ID. Sui pins a struct's type to its defining package, so an
 * upgrade moves the call target while every object stays typed to the original.
 * See the field docs in config/sui-networks.ts.
 */
export const ORIGINAL_PACKAGE_ID = IDS.originalPackageId;
export const LOYALTY_PROGRAM_ID = IDS.loyaltyProgramId;
export const BADGE_REGISTRY_ID = IDS.badgeRegistryId;
export const SHOP_REGISTRY_ID = IDS.shopRegistryId;
// '' until create_stamp_authority is called on this network — see the field
// comment on SuiNetworkIds. Callers must check for '' before building an
// attested tx (stamp-service.ts does this).
export const STAMP_AUTHORITY_ID = IDS.stampAuthorityId;

// Sui clock object (shared system object)
export const SUI_CLOCK_ID = '0x6';

// Module names
export const MODULES = {
    LOYALTY: 'loyalty_program',
    SHOP_REGISTRY: 'coffee_shop_registry',
    VOUCHER: 'voucher_system',
    BADGES: 'nft_badges',
    REFERRAL: 'referral_system',
} as const;

// Object types for filtering
export const OBJECT_TYPES = {
    CustomerProfile: `${ORIGINAL_PACKAGE_ID}::${MODULES.LOYALTY}::CustomerProfile`,
    CustomerShopAccount: `${ORIGINAL_PACKAGE_ID}::${MODULES.LOYALTY}::CustomerShopAccount`,
    RewardNFT: `${ORIGINAL_PACKAGE_ID}::${MODULES.LOYALTY}::RewardNFT`,
    LoyaltyProgram: `${ORIGINAL_PACKAGE_ID}::${MODULES.LOYALTY}::LoyaltyProgram`,
    Purchase: `${ORIGINAL_PACKAGE_ID}::${MODULES.LOYALTY}::Purchase`,
    CoffeeShop: `${ORIGINAL_PACKAGE_ID}::${MODULES.SHOP_REGISTRY}::CoffeeShop`,
    ShopRegistry: `${ORIGINAL_PACKAGE_ID}::${MODULES.SHOP_REGISTRY}::ShopRegistry`,
    ShopCapability: `${ORIGINAL_PACKAGE_ID}::${MODULES.SHOP_REGISTRY}::ShopCapability`,
    Voucher: `${ORIGINAL_PACKAGE_ID}::${MODULES.VOUCHER}::Voucher`,
    Badge: `${ORIGINAL_PACKAGE_ID}::${MODULES.BADGES}::Badge`,
    BadgeRegistry: `${ORIGINAL_PACKAGE_ID}::${MODULES.BADGES}::BadgeRegistry`,
    Referral: `${ORIGINAL_PACKAGE_ID}::${MODULES.REFERRAL}::Referral`,
    ReferralStats: `${ORIGINAL_PACKAGE_ID}::${MODULES.REFERRAL}::ReferralStats`,
} as const;

// Event types for subscription
export const EVENT_TYPES = {
    CustomerRegistered: `${ORIGINAL_PACKAGE_ID}::${MODULES.LOYALTY}::CustomerRegistered`,
    PurchaseRecorded: `${ORIGINAL_PACKAGE_ID}::${MODULES.LOYALTY}::PurchaseRecorded`,
    PurchaseRequestEvent: `${ORIGINAL_PACKAGE_ID}::${MODULES.LOYALTY}::PurchaseRequestEvent`,
    LevelUpEvent: `${ORIGINAL_PACKAGE_ID}::${MODULES.LOYALTY}::LevelUpEvent`,
    CustomerShopAccountCreated: `${ORIGINAL_PACKAGE_ID}::${MODULES.LOYALTY}::CustomerShopAccountCreated`,
    RewardRedeemed: `${ORIGINAL_PACKAGE_ID}::${MODULES.LOYALTY}::RewardRedeemed`,
    ShopRegistered: `${ORIGINAL_PACKAGE_ID}::${MODULES.SHOP_REGISTRY}::ShopRegistered`,
    ShopVerified: `${ORIGINAL_PACKAGE_ID}::${MODULES.SHOP_REGISTRY}::ShopVerified`,
    VoucherMinted: `${ORIGINAL_PACKAGE_ID}::${MODULES.VOUCHER}::VoucherMinted`,
    VoucherRedeemed: `${ORIGINAL_PACKAGE_ID}::${MODULES.VOUCHER}::VoucherRedeemed`,
    BadgeMinted: `${ORIGINAL_PACKAGE_ID}::${MODULES.BADGES}::BadgeMinted`,
    ReferralCreated: `${ORIGINAL_PACKAGE_ID}::${MODULES.REFERRAL}::ReferralCreated`,
} as const;

// Points config (should match on-chain values)
export const POINTS_CONFIG = {
    pointsPerPurchase: 10,
    pointsForFreeCoffee: 100,
    referralBonus: 20,
    referralPurchasesRequired: 3,
    minTimeBetweenPurchases: 300000, // 5 minutes in ms
} as const;
